import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("nibro_cart") || "[]");
    } catch {
      return [];
    }
  });
  // A short-lived "pack tip" the floating PackTipToast component reads from.
  // Set whenever an add results in a non-multiple-of-pack quantity (so the
  // user gets an in-context nudge BEFORE navigating to the cart).
  const [packTip, setPackTip] = useState(null);

  useEffect(() => {
    localStorage.setItem("nibro_cart", JSON.stringify(items));
  }, [items]);

  // Recompute unit_price based on qty:
  //  - new model (Omdoos-korting): discount applies ONLY on exact multiples of
  //    box_qty (75 / 150 / 225 / ...). 76 stuks = stukprijs (1 omdoos + 1 los).
  //  - legacy fallback for any items still using pack_size/bundle/single
  const effectiveUnitPrice = (item, qty) => {
    const meta = item.meta || {};
    if (meta.box_qty && meta.box_unit_price != null && meta.piece_price != null) {
      const aligned = qty >= meta.box_qty && qty % meta.box_qty === 0;
      return aligned ? meta.box_unit_price : meta.piece_price;
    }
    const pack = meta.pack_size || 1;
    if (pack <= 1) return item.unit_price;
    const bundle = meta.bundle_price;
    const single = meta.single_piece_price;
    if (bundle == null || single == null) return item.unit_price;
    return qty >= pack ? bundle : single;
  };

  const addItem = (item) => {
    setItems((prev) => {
      // For custom configured items, always add as new line
      if (item.config) return [...prev, { ...item, lineId: Date.now() + Math.random() }];
      const existing = prev.find((p) => p.product_id === item.product_id && p.sku === item.sku && !p.config);
      if (existing) {
        return prev.map((p) => {
          if (p.lineId !== existing.lineId) return p;
          const newQty = p.quantity + item.quantity;
          return { ...p, quantity: newQty, unit_price: effectiveUnitPrice(p, newQty) };
        });
      }
      return [...prev, { ...item, lineId: Date.now() + Math.random() }];
    });
    // Smart pack-tip: if the resulting quantity is NOT a whole multiple of
    // pack_size we surface a toast suggesting the closest VE multiple.
    // Computed AFTER the setItems above so we know the final qty.
    const meta = item.meta || {};
    const ps = Number(meta.pack_size || 0);
    if (ps > 1) {
      // Resulting qty after add: same SKU collapse OR brand-new line.
      const existingItem = items.find(
        (p) => p.product_id === item.product_id && p.sku === item.sku && !p.config,
      );
      const resultingQty = (existingItem ? existingItem.quantity : 0) + item.quantity;
      if (resultingQty > 0 && resultingQty % ps !== 0) {
        const target = Math.ceil(resultingQty / ps) * ps; // next VE multiple
        setPackTip({
          id: Date.now() + Math.random(),
          sku: item.sku,
          product_id: item.product_id,
          fromQty: resultingQty,
          target,
          packSize: ps,
          unitPrice: Number(item.unit_price || 0),
          subpackFee: Number(meta.subpack_surcharge ?? 24.5),
        });
      }
    }
  };

  const dismissPackTip = useCallback(() => setPackTip(null), []);

  const acceptPackTip = useCallback(() => {
    if (!packTip) return;
    setItems((prev) =>
      prev.map((p) => {
        if (p.sku !== packTip.sku || p.product_id !== packTip.product_id) return p;
        return { ...p, quantity: packTip.target, unit_price: effectiveUnitPrice(p, packTip.target) };
      }),
    );
    setPackTip(null);
  }, [packTip]);

  const updateQty = (lineId, qty) => {
    setItems((prev) =>
      prev.map((p) => {
        if (p.lineId !== lineId) return p;
        const newQty = Math.max(1, qty);
        return { ...p, quantity: newQty, unit_price: effectiveUnitPrice(p, newQty) };
      })
    );
  };

  const removeItem = (lineId) => {
    setItems((prev) => prev.filter((p) => p.lineId !== lineId));
  };

  const clear = () => setItems([]);

  // Per-line €24.50 split-pack surcharge: applied whenever the ordered
  // quantity is NOT a whole multiple of the variant's pack_size
  // (verpakkingseenheid). Examples for VE=10:
  //   • qty=8   → +€24.50 (sub-pack)
  //   • qty=10  → no surcharge (exact 1 pack)
  //   • qty=12  → +€24.50 (10 + 2 loose)
  //   • qty=20  → no surcharge (exact 2 packs)
  // Mirrors backend `/api/orders` so the cart total matches the invoice.
  const lineSurcharge = (item) => {
    const meta = item.meta || {};
    const packSize = Number(meta.pack_size || 0);
    const fee = Number(meta.subpack_surcharge ?? 24.5);
    if (packSize > 1 && item.quantity > 0 && item.quantity % packSize !== 0 && fee > 0) {
      return fee;
    }
    return 0;
  };

  const subtotal = items.reduce(
    (s, i) => s + i.quantity * i.unit_price + lineSurcharge(i),
    0
  );
  const surchargeTotal = items.reduce((s, i) => s + lineSurcharge(i), 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items, addItem, updateQty, removeItem, clear, subtotal,
        surchargeTotal, lineSurcharge, count,
        packTip, dismissPackTip, acceptPackTip,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
